// components/Cart.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  X, 
  Plus, 
  Minus, 
  Trash2,
  ArrowRight,
  Tag
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import '../styles/Cart.css';

const Cart = ({ isOpen, onClose, user }) => {
  const navigate = useNavigate();
  const { 
    items, 
    updateQuantity, 
    removeFromCart, 
    clearCart, 
    getCartTotal, 
    calculateItemTotal,
    getQuantityPricing,
    getDiscountAmount 
  } = useCart();

  const handleQuantityChange = (id, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(id);
    } else if (newQuantity <= 4) {
      updateQuantity(id, newQuantity);
    }
  };

  const handleCheckout = () => {
    if (!user) {
      navigate('/', { 
        state: { 
          message: 'Please login to proceed with booking',
          showAuth: true 
        } 
      });
      return;
    }

    if (items.length === 0) {
      return;
    }

    // Navigate to booking with cart items
    navigate('/booking', { 
      state: { 
        cartItems: items,
        isCartBooking: true 
      } 
    });
    onClose();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN').format(price);
  };

  if (!isOpen) return null;

  return (
    <div className="cart-overlay" onClick={onClose}>
      <div className="cart-sidebar" onClick={(e) => e.stopPropagation()}>
        <div className="cart-header">
          <div className="cart-title">
            <ShoppingCart size={24} />
            <h2>Your Cart ({items.length})</h2>
          </div>
          <button className="cart-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="cart-content">
          {items.length === 0 ? (
            <div className="empty-cart">
              <ShoppingCart size={48} />
              <h3>Your cart is empty</h3>
              <p>Add some services to get started</p>
              <button className="browse-services-btn" onClick={() => {
                navigate('/services');
                onClose();
              }}>
                Browse Services
              </button>
            </div>
          ) : (
            <>
              <div className="cart-items">
                {items.map((item) => {
                  const pricing = getQuantityPricing(item.name, item.basePrice);
                  const itemTotal = calculateItemTotal(item);
                  const discount = getDiscountAmount(item.name, item.basePrice, item.quantity, item);
                  const regularPrice = item.basePrice * item.quantity;

                  return (
                    <div key={item.id} className="cart-item">
                      <div className="item-info">
                        <h4>{item.name}</h4>
                        <p className="item-description">
                          {item.description?.length > 60 
                            ? `${item.description.substring(0, 60)}...` 
                            : item.description}
                        </p>
                        <div className="item-meta">
                          <span className="duration">{item.duration}</span>
                          <span className="warranty">{item.warranty}</span>
                        </div>
                      </div>

                      <div className="item-controls">
                        <div className="quantity-controls">
                          <button 
                            className="qty-btn"
                            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          >
                            <Minus size={16} />
                          </button>
                          <span className="quantity">{item.quantity}</span>
                          <button 
                            className="qty-btn"
                            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            disabled={item.quantity >= 4}
                          >
                            <Plus size={16} />
                          </button>
                        </div>

                        <div className="item-pricing">
                          {discount > 0 && (
                            <div className="discount-info">
                              <span className="original-price">₹{formatPrice(regularPrice)}</span>
                              <div className="discount-badge">
                                <Tag size={12} />
                                ₹{formatPrice(discount)} OFF
                              </div>
                            </div>
                          )}
                          <div className="item-total">₹{formatPrice(itemTotal)}</div>
                        </div>

                        <button 
                          className="remove-btn"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="cart-footer">
                <div className="cart-summary">
                  <div className="total-savings">
                    {items.some(item => getDiscountAmount(item.name, item.basePrice, item.quantity, item) > 0) && (
                      <div className="savings-info">
                        <Tag size={16} />
                        <span>
                          You're saving ₹{formatPrice(
                            items.reduce((total, item) => 
                              total + getDiscountAmount(item.name, item.basePrice, item.quantity, item), 0
                            )
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="cart-total">
                    <span>Total: ₹{formatPrice(getCartTotal())}</span>
                  </div>
                </div>

                <div className="cart-actions">
                  <button 
                    className="clear-cart-btn"
                    onClick={clearCart}
                  >
                    Clear Cart
                  </button>
                  <button 
                    className="checkout-btn"
                    onClick={handleCheckout}
                  >
                    {!user ? 'Login to Book' : 'Proceed to Book'}
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;